// --- FAQComponent.jsx (Using Material-UI Components) ---
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import SpaIcon from '@mui/icons-material/Spa';
import PaidIcon from '@mui/icons-material/Paid';
import Header from '../components/Header';
import Footer from '../components/Footer';

const FAQComponent = () => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
      <>
      <Header />
    <Box sx={styles.faqContainer}>
      <Typography variant="h3" component="h1" gutterBottom sx={styles.header}>
        Frequently Asked Questions (FAQ)
      </Typography>
      
      <Typography variant="body1" paragraph>
        Find quick answers to common questions about our fresh produce, delivery, and payment policies in Chennai.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* --- Section 1: Product Quality (Meat, Fruits, Vegetables) --- */}
      <Typography variant="h5" component="h2" gutterBottom sx={styles.sectionHeader}>
        <SpaIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Product Quality & Selection
      </Typography>
      
      <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')} sx={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <Typography sx={{ fontWeight: 'bold' }}>How do you ensure the freshness of fruits and vegetables?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">
            Our expert pickers hand-select produce the morning of your scheduled delivery from the freshest available stock. We check for color, firmness, and ripeness. All items are stored in temperature-controlled environments before being packed into your order and delivered in cold-chain boxes to maintain quality until they reach your door.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')} sx={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2a-content" id="panel2a-header">
          <Typography sx={{ fontWeight: 'bold' }}>What is the quality and source of your meat?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">
            Our meat (primarily water buffalo and poultry) is sourced from FSSAI-approved, hygienic, and licensed abattoirs in Tamil Nadu, including our main supplier near Perambur. All meat is cut fresh daily, vacuum-sealed or packed in temperature-stable packaging, and maintained at sub-zero temperatures throughout the delivery process.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')} sx={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel3a-content" id="panel3a-header">
          <Typography sx={{ fontWeight: 'bold' }}>Can I specify the ripeness of fruits (e.g., green bananas)?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">
            Yes! During the checkout process, there is a **"Notes"** section for fresh items. You can request specific ripeness levels (e.g., "fully ripe avocados," "slightly green mangoes"). Our pickers will do their absolute best to fulfill these requests based on available stock.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 3 }} />

      {/* --- Section 2: Ordering and Pricing --- */}
      <Typography variant="h5" component="h2" gutterBottom sx={styles.sectionHeader}>
        <ShoppingBasketIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Ordering & Pricing
      </Typography>

      <Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')} sx={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel4a-content" id="panel4a-header">
          <Typography sx={{ fontWeight: 'bold' }}>How do you handle items sold by weight (e.g., meat, grapes)?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">
            Prices for weighable items are **estimated** at checkout based on the approximate weight you request (e.g., 500g). The final price will be calculated based on the **exact weight** of the item as it is packed by our team. Your final invoice, updated on delivery, will reflect the precise charge. This may lead to a small variation in the final bill.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'panel5'} onChange={handleChange('panel5')} sx={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel5a-content" id="panel5a-header">
          <Typography sx={{ fontWeight: 'bold' }}>What happens if an item I ordered is out of stock?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">
            If an item is out of stock, we will attempt to find a **suitable substitution** of equal or better quality, and the same or lower price, unless you explicitly opted out of substitutions at checkout. If no substitution is available, the item will be removed from your bill, and a full refund will be processed immediately.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 3 }} />

      {/* --- Section 3: Delivery and Returns (Linking to Policies) --- */}
      <Typography variant="h5" component="h2" gutterBottom sx={styles.sectionHeader}>
        <PaidIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Delivery & Service
      </Typography>

      <Accordion expanded={expanded === 'panel6'} onChange={handleChange('panel6')} sx={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel6a-content" id="panel6a-header">
          <Typography sx={{ fontWeight: 'bold' }}>Is there a minimum order value?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">
            Yes, the minimum order value is **₹200**. Orders above **₹699** qualify for free delivery. All other orders incur a flat delivery fee of ₹49.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion expanded={expanded === 'panel7'} onChange={handleChange('panel7')} sx={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel7a-content" id="panel7a-header">
          <Typography sx={{ fontWeight: 'bold' }}>What if I receive a damaged or spoiled item?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">
            Please **reject the item immediately** during the delivery inspection. If you reject an item, we will issue a full refund or store credit within 2 hours. Due to the perishable nature of our goods, we cannot accept returns once the delivery executive has left your premises. Please refer to our detailed **Return and Refund Policy** for more information.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
    </Box>
          <Footer />
              </>
  );
};

// MUI Styles for the component
const styles = {
  faqContainer: {
    padding: 4,
    maxWidth: 850,
    margin: '40px auto',
    backgroundColor: '#f9f9f9',
    borderRadius: 2,
    boxShadow: 3,
  },
  header: {
    color: '#00704A',
    borderBottom: '2px solid #EEE',
    paddingBottom: 1,
    marginBottom: 2,
  },
  sectionHeader: {
    color: '#333', 
    fontWeight: 'bold',
    mt: 3,
    mb: 1,
    borderLeft: '4px solid #00704A',
    paddingLeft: 1,
  },
  accordion: {
    marginBottom: 1,
    border: '1px solid #ddd',
    '&.Mui-expanded': {
      backgroundColor: '#f0fff0',
    }
  }
};

export default FAQComponent;